import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ORDER_STATUS_LABEL,
  formatYuan,
  getOrder,
  listAssets,
  listJobs,
  type Asset,
  type Job,
  type Order,
} from '../lib/api';

export default function OrderDetail() {
  const { code = '' } = useParams();
  const [order, setOrder] = useState<Order | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const o = await getOrder(code);
        setOrder(o);
        const [j, a] = await Promise.all([
          listJobs(code).catch(() => ({ jobs: [] as Job[] })),
          listAssets(code).catch(() => ({ assets: [] as Asset[] })),
        ]);
        setJobs(j.jobs);
        setAssets(a.assets);
      } catch {
        setError('订单不存在或加载失败');
      }
    })();
  }, [code]);

  const nextLink = (() => {
    if (!order) return null;
    if (order.status === 'pending_payment') {
      return { to: `/order/${code}/pay`, label: '去支付' };
    }
    if (order.status === 'pending_confirm') {
      return { to: `/order/${code}/xianyu`, label: '查看 / 修改闲鱼单号' };
    }
    if (order.status === 'paid') {
      return { to: `/order/${code}/studio`, label: '进入创作室' };
    }
    return null;
  })();

  const outputs = assets.filter((a) => a.kind === 'output');

  return (
    <section className="stack">
      <div className="work-card">
        <h2 className="type-display-md text-ink">订单详情</h2>
        {error ? <div className="error">{error}</div> : null}
        {!order && !error ? <p className="type-body-md text-ink-secondary">加载中…</p> : null}
        {order ? (
          <>
            <p className="type-body-md text-ink">
              订单号：<span className="mono">{order.public_code}</span>
            </p>
            <p className="type-body-md text-ink">
              状态：
              <span className={`status-pill ${order.status}`}>
                {ORDER_STATUS_LABEL[order.status] ?? order.status}
              </span>
            </p>
            <p className="type-body-md text-ink">
              档位：{order.tier === 'deep' ? '深度' : '标准'} · 金额{' '}
              {formatYuan(order.amount_cents)}
            </p>
            {order.xianyu_order_no ? (
              <p className="type-body-md text-ink">
                闲鱼订单号：<span className="mono">{order.xianyu_order_no}</span>
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      {nextLink ? (
        <div className="row">
          <Link className="btn-primary" to={nextLink.to}>
            {nextLink.label}
          </Link>
        </div>
      ) : null}

      {jobs.length > 0 ? (
        <div className="work-card">
          <h2 className="type-display-md text-ink">生成任务</h2>
          <ul className="steps">
            {jobs.map((j) => (
              <li key={j.id}>
                <span className="mono">{j.id.slice(0, 8)}</span> · {j.status}
                {j.error ? ` · ${j.error}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {outputs.length > 0 ? (
        <div className="work-card stack">
          <h2 className="type-display-md text-ink">成品</h2>
          {outputs.map((a) => (
            <div key={a.id}>
              <img className="preview" src={a.preview_url} alt="成品" />
              <p className="type-body-md text-ink">
                <a href={a.preview_url} target="_blank" rel="noreferrer">
                  打开 / 下载
                </a>
              </p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="row">
        <Link className="btn-secondary" to="/">
          回首页
        </Link>
      </div>
    </section>
  );
}
